import { object, string } from "prop-types";
import { Suspense, lazy, memo, useCallback, useEffect } from "react";

import styled from "styled-components/macro";
import { MAX_MODAL_PX_HEIGHT, MAX_MODAL_VH } from "theme/interpolations/height";

import flocknoteStateHooks from "Flocknote/stateHooks";

import FnrCard from "UI/FnrCard/FnrCard";
import FnrCloseButton from "UI/FnrCloseButton";
import FnrModal from "UI/FnrModal";
import RunningUno from "UI/RunningUno";
import { scrollbarInvisibleThin } from "UI/scrollbarStyles";

import configsByContentKey from "./configsByContentKey";
import {
  ASK_THE_MEMBER,
  CAMPAIGNS_SPLASH,
  CONTACT_HISTORY,
  CREATE_ADDON_GIFT,
  CREATE_GROUP,
  CREATE_PRAYER_REQUEST,
  CTA_EDITOR,
  DONOR_SUMMARY_REPORTS,
  EXTENDED_FIELDS,
  EXTENDED_FIELD_HISTORY,
  FAMILY_REGISTRATION_SPLASH,
  FAMILY_RELATIONSHIP_PICKER,
  FNC_SMART_GROUP_PROMO,
  FNC_SMART_GROUP_UPGRADE_GROUP_LIMIT,
  FNC_UPGRADE,
  GIFT_REFUND,
  GIVING_SPLASH,
  HOUSEHOLD_DB_VIDEO,
  LEGAL_ENTITY_INFO,
  MANAGE_MY_GIVING_SPLASH,
  MANAGE_PROXY_CONTACTS,
  PREPARING_EXPORT,
  SACRAMENT_HISTORY,
  SACRAMENT_TRACKING,
  SMALL_CHURCH_ATTESTATION,
  STAX_KYC,
  STAX_STATUS,
  TRANSFER_PARTIAL_CONTACT_VIEW_IN_SHEET,
  TTJ_ACTION_FLOW,
  TWO_FA_DISENROLLMENT,
  TWO_FA_ENROLLMENT,
} from "./contentKeys";

const ModalContents = memo(({ contentKey, contentProps }) => {
  let Component;
  // Add additional case statements here for the components you'd like to add.
  switch (contentKey) {
    case EXTENDED_FIELDS:
      Component = lazy(() => import("ExtendedFields/ExtendedFields"));
      break;
    case CREATE_ADDON_GIFT:
      Component = lazy(() => import("Giving/CreateAddOnGift"));
      break;
    case CTA_EDITOR:
      Component = lazy(() => import("Giving/FundItSettings/CtaEditor"));
      break;
    case STAX_KYC:
      Component = lazy(() => import("Giving/StaxKYC"));
      break;
    case STAX_STATUS:
      Component = lazy(() => import("Giving/StaxStatus"));
      break;
    case GIFT_REFUND:
      Component = lazy(() =>
        import("Giving/GiftRefundOverlay/GiftRefundOverlay")
      );
      break;
    case FNC_UPGRADE:
      Component = lazy(() => import("Giving/FNCUpgradeOverlay"));
      break;
    case SACRAMENT_HISTORY:
      Component = lazy(() => import("History/SacramentHistory"));
      break;
    case HOUSEHOLD_DB_VIDEO:
      Component = lazy(() =>
        import("Dashboard/DashboardMainSections/HouseholdDBSplash/VideoModal")
      );
      break;
    case SACRAMENT_TRACKING:
      Component = lazy(() => import("SacramentTracking/SacramentForm"));
      break;
    case TWO_FA_ENROLLMENT:
      Component = lazy(() => import("UserProfile/TwoFAEnrollment"));
      break;
    case TWO_FA_DISENROLLMENT:
      Component = lazy(() => import("UserProfile/TwoFA/TwoFADisenrollment"));
      break;
    case MANAGE_PROXY_CONTACTS:
      Component = lazy(() => import("ManageProxyContacts/ManageProxyContacts"));
      break;
    case FAMILY_RELATIONSHIP_PICKER:
      Component = lazy(() =>
        import("FamilyRelationshipPicker/FamilyRelationshipPicker")
      );
      break;
    case DONOR_SUMMARY_REPORTS:
      Component = lazy(() =>
        import("Giving/TaxSummaryEOY/DonorSummaryReports")
      );
      break;
    case EXTENDED_FIELD_HISTORY:
      Component = lazy(() => import("History/ExtendedFieldHistory"));
      break;
    case CONTACT_HISTORY:
      Component = lazy(() => import("History/ContactHistory"));
      break;
    case ASK_THE_MEMBER:
      Component = lazy(() =>
        import(
          "CrossFeatureComponents/AskTheMember/AskTheMemberModal/AskTheMemberModal"
        )
      );
      break;
    case CREATE_GROUP:
      Component = lazy(() =>
        import("CrossFeatureComponents/CreateGroup/CreateGroup")
      );
      break;
    case FNC_SMART_GROUP_PROMO:
      Component = lazy(() => import("Group/FNCSmartGroupPromo"));
      break;
    case FAMILY_REGISTRATION_SPLASH:
      Component = lazy(() => import("splashes/FamilyRegistrationSplash"));
      break;
    case GIVING_SPLASH:
      Component = lazy(() => import("splashes/GivingSplash"));
      break;
    case CAMPAIGNS_SPLASH:
      Component = lazy(() => import("splashes/CampaignsSplash"));
      break;
    case MANAGE_MY_GIVING_SPLASH:
      Component = lazy(() => import("splashes/ManageMyGivingSplash"));
      break;
    case FNC_SMART_GROUP_UPGRADE_GROUP_LIMIT:
      Component = lazy(() =>
        import("Group/FNCSmartGroupPromo/FNCSmartGroupUpgrade")
      );
      break;
    case PREPARING_EXPORT:
      Component = lazy(() => import("Export/PreparingExportModal"));
      break;
    case LEGAL_ENTITY_INFO:
      Component = lazy(() =>
        import("CrossFeatureComponents/LegalEntityInfo/LegalEntityInfo")
      );
      break;
    case CREATE_PRAYER_REQUEST:
      Component = lazy(() =>
        import(
          "PrayerRequests/components/CreatePrayerRequest/CreatePrayerRequest"
        )
      );
      break;
    case SMALL_CHURCH_ATTESTATION:
      Component = lazy(() => import("NumberManagement/SmallChurchAttestation"));
      break;
    case TTJ_ACTION_FLOW:
      Component = lazy(() =>
        import("CrossFeatureComponents/TTJActionFlow/TTJActionFlow")
      );
      break;
    case TRANSFER_PARTIAL_CONTACT_VIEW_IN_SHEET:
      Component = lazy(() =>
        import(
          "HappeTools/Transfers/screens/PartialContactMatches/ViewInSheet/ViewInSheet"
        )
      );
      break;
    default:
      Component = () => null;
  }

  return (
    <Suspense fallback={<RunningUno />}>
      <Component {...contentProps} isModal />
    </Suspense>
  );
});

ModalContents.propTypes = { contentKey: string, contentProps: object };

const overflow = ({ $flexHeight }) => {
  if ($flexHeight) return `overflow-y: auto;`;
  return `overflow-y: hidden;`;
};

const MAX_MODAL_VW = "90vw";
const MAX_MODAL_PX_WIDTH = "800px";

const ContentContainer = styled(FnrCard)`
  ${({
    theme: {
      palette: { grey },
    },
    $flexHeight,
    $flexWidth,
    $maxWidth = MAX_MODAL_PX_WIDTH,
    $maxHeight = `${MAX_MODAL_PX_HEIGHT}px`,
    $minHeight,
    $legacy,
  }) => `
  ${
    $flexWidth
      ? `max-width: ${MAX_MODAL_VW};`
      : `width: min(${$maxWidth}, ${MAX_MODAL_VW});`
  }
  ${
    $flexHeight
      ? `max-height: ${MAX_MODAL_VH}vh;`
      : `height: ${`min(${$maxHeight}, ${MAX_MODAL_VH}vh)`};`
  }
  ${$legacy ? "margin: 8px;" : ""}
  ${$minHeight ? `min-height: ${$minHeight};` : ""}
  background-color: ${grey};`}
  position: relative;
  ${overflow}
  ${scrollbarInvisibleThin}
  scrollbar-width: none;
`;

const Modal = () => {
  const [{ isOpen, contentKey, contentProps }, { closeModal }] =
    flocknoteStateHooks.useModal();

  const handleClose = useCallback(() => {
    // pass in onClose as a special contentProp to fire whatever you need when the modal closes:
    // openModal('someKey',' {onClose: () => {}})
    if (contentProps?.onClose) contentProps.onClose();
    closeModal();
  }, [contentProps, closeModal]);

  const handleBackgroundClick = (event) => {
    // Ensure the click is on the background and not inside the modal content
    if (event.target.getAttribute("data-cy") === "fnrmodalbackground") {
      if (configsByContentKey.closeOnBackgroundClick[contentKey]) closeModal();
    }
  };
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  const flexHeight = configsByContentKey.flexHeight[contentKey];
  const flexWidth = configsByContentKey.flexWidth[contentKey];
  const maxWidth = configsByContentKey.maxWidth[contentKey];
  const maxHeight = configsByContentKey.maxHeight[contentKey];
  const minHeight = configsByContentKey.minHeight[contentKey];
  const legacy = configsByContentKey.legacy[contentKey];
  const closeButtonPosition =
    configsByContentKey.closeButtonPosition[contentKey];

  return (
    <FnrModal
      show={isOpen}
      data-cy="Global_Modal"
      onBackgroundClick={handleBackgroundClick}
    >
      {closeButtonPosition === "hide" ? null : (
        <FnrCloseButton
          onClick={handleClose}
          aria-label="close-modal"
          legacy={legacy}
          position={closeButtonPosition}
        />
      )}
      <ContentContainer
        data-cy="Modal_Container"
        $flexHeight={flexHeight}
        $flexWidth={flexWidth}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $minHeight={minHeight}
        variant="modal"
        $legacy={legacy}
      >
        <ModalContents contentProps={contentProps} contentKey={contentKey} />
      </ContentContainer>
    </FnrModal>
  );
};

export { MAX_MODAL_VW, MAX_MODAL_PX_WIDTH };
export default Modal;
